import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShieldCheck, X } from 'lucide-react';

/**
 * Persistent floating CTA injected by StudyAbroadLayout.
 * Hides itself when the user is already on the eligibility checker page
 * or has explicitly dismissed it for the session.
 */
const StickyEligibilityCTA = () => {
  const location = useLocation();
  // Lazy-init from sessionStorage so we never flash the CTA after dismissal
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('sa-sticky-cta-dismissed') === '1';
  });
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Mount-fade-in after small delay so it doesn't compete with hero
    const t = setTimeout(() => setShow(true), 1200);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Auto-hide on the eligibility / book-counselling pages
  // and on country-hub pages (which ship their own purpose-built mobile sticky CTA bar)
  const path = location.pathname;
  const HUB_SUPPRESSIONS = [
    '/study-abroad/uk',
    '/study-abroad/canada',
    '/study-abroad/germany',
    '/study-abroad/australia',
    '/study-abroad/ireland',
    '/study-abroad/new-zealand',
    '/study-abroad/uae',
    '/study-abroad/dubai',
  ];
  const onHubPage = HUB_SUPPRESSIONS.some(
    (p) => path === p || path === `${p}/`,
  );
  if (path.includes('/visa-eligibility') || path.includes('/book-counselling')) return null;
  if (onHubPage) return null;
  if (dismissed) return null;

  return (
    <div
      className={`fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-[60] transition-all duration-500 ${
        show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
      }`}
      data-testid="sa-sticky-eligibility-cta"
    >
      <div className="relative bg-gradient-to-r from-[#003087] to-[#001F5C] rounded-full shadow-2xl border border-[#C5A028]/40 pl-2 pr-5 py-2 flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            sessionStorage.setItem('sa-sticky-cta-dismissed', '1');
            setDismissed(true);
          }}
          className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          data-testid="sa-sticky-cta-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-3 h-3 text-white/70" />
        </button>
        <Link
          to="/study-abroad/visa-eligibility/"
          className="flex items-center gap-2 text-sm font-bold text-white whitespace-nowrap"
          data-testid="sa-sticky-cta-link"
        >
          <span className="w-8 h-8 rounded-full bg-[#C5A028] flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-black" />
          </span>
          Check Your Eligibility
          <span className="text-[10px] text-[#C5A028] font-semibold">→</span>
        </Link>
      </div>
    </div>
  );
};

export default StickyEligibilityCTA;
